import type { EquipSlot, ItemAffix, ItemInstance, ItemSlotType, RunFoundItem } from '../api/types';

/**
 * Display formatting for items (persistent stash items and temporary run loot). Pure
 * helpers, so the inventory/equipment panels render a faithful projection of the
 * item model (rarity, slot, affixes, durability, seal depth, account_bound).
 */

const AFFIX_LABEL: Record<string, string> = {
  attack: 'ATK',
  defense: 'DEF',
  max_hp: 'Max HP',
  max_lantern: 'Max Lantern',
  oil_restore_bonus: 'Oil restore',
  torch_restore_bonus: 'Torch restore',
  potion_heal_bonus: 'Potion heal',
  burden_reduction: 'Burden load',
  lantern_efficiency_bps: 'Lantern drain',
};

function signed(value: number): string {
  return value >= 0 ? `+${value}` : String(value);
}

function percentFromBps(value: number): string {
  return (Math.abs(value) / 100).toFixed(1).replace(/\.0$/, '');
}

export function rarityLabel(rarity: string): string {
  return rarity.charAt(0).toUpperCase() + rarity.slice(1);
}

export function slotLabel(slot: ItemSlotType | EquipSlot | string): string {
  switch (slot) {
    case 'trinket1': return 'Trinket I';
    case 'trinket2': return 'Trinket II';
    default: return slot.charAt(0).toUpperCase() + slot.slice(1);
  }
}

/** Slots a given item slot_type is compatible with (mirrors backend isSlotCompatible). */
export function compatibleSlots(slotType: ItemSlotType): EquipSlot[] {
  if (slotType === 'trinket') return ['trinket1', 'trinket2'];
  return [slotType];
}

export function formatItemEffect(affix: ItemAffix): string {
  if (affix.stat === 'lantern_efficiency_bps') return `${AFFIX_LABEL[affix.stat]} -${percentFromBps(affix.value)}%`;
  if (affix.stat === 'burden_reduction') return `${AFFIX_LABEL[affix.stat]} -${affix.value}`;
  return `${AFFIX_LABEL[affix.stat] ?? affix.stat} ${signed(affix.value)}`;
}

function affixList(affixes: unknown): string[] {
  if (!Array.isArray(affixes)) return [];
  return affixes
    .filter((a): a is ItemAffix => !!a && typeof a === 'object' && 'stat' in a && 'value' in a)
    .map(formatItemEffect);
}

export function affixSummary(affixes: unknown): string {
  const list = affixList(affixes);
  return list.length > 0 ? list.join(' · ') : 'no affixes';
}

export function durabilityText(cur: number, max: number): string {
  return `${cur}/${max}${cur === 0 ? ' (broken)' : ''}`;
}

export interface ItemView {
  title: string;
  rarity: string;
  slotType: ItemSlotType;
  affixes: string[];
  durability: string;
  broken: boolean;
  sealText: string;
  flags: string[];
}

/** A normalized view of a persistent stash item. */
export function describeItem(item: ItemInstance): ItemView {
  const flags: string[] = [];
  if (item.accountBound) flags.push('account-bound');
  if (item.sealState === 'sealed') flags.push('sealed');
  return {
    title: `${rarityLabel(item.rarity)} ${slotLabel(item.slotType)}`,
    rarity: item.rarity,
    slotType: item.slotType,
    affixes: affixList(item.affixes),
    durability: durabilityText(item.durabilityCur, item.durabilityMax),
    broken: item.durabilityCur === 0,
    sealText: `seal depth ${item.sealDepth}`,
    flags,
  };
}

/** A normalized view of temporary in-run loot. */
export function describeRunItem(item: RunFoundItem): ItemView {
  const flags: string[] = [];
  if (item.accountBound) flags.push('account-bound');
  flags.push(item.state);
  return {
    title: `${rarityLabel(item.rarity)} ${slotLabel(item.slotType)}`,
    rarity: item.rarity,
    slotType: item.slotType,
    affixes: affixList(item.affixes),
    durability: durabilityText(item.durabilityCur, item.durabilityMax),
    broken: item.durabilityCur === 0,
    sealText: `needs floor ≥ ${item.sealDepth} to seal`,
    flags,
  };
}
